import React from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { TagInputField } from '../fields/TagInputField';

interface CapabilitiesSectionProps {
  providerInfo: any;
  form?: any;
  setForm?: React.Dispatch<React.SetStateAction<any>>;
  saving?: boolean;
  readonly?: boolean;
}

export const CapabilitiesSection = ({
  providerInfo,
  form,
  setForm,
  saving = false,
  readonly = false,
}: CapabilitiesSectionProps) => {
  const handleFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (setForm) {
      setForm((prev: any) => ({ ...prev, [e.target.name]: e.target.value }));
    }
  };

  const handleSwitchChange = (fieldName: string, checked: boolean) => {
    if (setForm) {
      setForm((prev: any) => ({ ...prev, [fieldName]: checked }));
    }
  };

  if (readonly) {
    return (
      <div className="space-y-6">
        <h3 className="text-lg font-semibold">Service Capabilities</h3>
        <div className="grid gap-6">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm font-medium text-muted-foreground">Mobile App</Label>
              <p className="text-xs text-muted-foreground">Does the PEO offer a mobile application?</p>
            </div>
            <div className={`w-3 h-3 rounded-full ${providerInfo.mobile_app ? 'bg-green-500' : 'bg-gray-300'}`} />
          </div>
          
          <div>
            <Label className="text-sm font-medium text-muted-foreground">Payroll Platform</Label>
            <p className="text-base mt-1">{providerInfo.payroll_platform || <span className="text-muted-foreground italic">Not set</span>}</p>
          </div>

          <div>
            <Label className="text-sm font-medium text-muted-foreground">Medical Carriers</Label>
            <div className="mt-2">
              {providerInfo.medical_carriers && providerInfo.medical_carriers.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {providerInfo.medical_carriers.map((carrier: string, index: number) => (
                    <span key={index} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {carrier}
                    </span>
                  ))}
                </div>
              ) : (
                <span className="text-muted-foreground italic">Not set</span>
              )}
            </div>
          </div>

          <div>
            <Label className="text-sm font-medium text-muted-foreground">Ancillary Carriers</Label>
            <div className="mt-2">
              {providerInfo.ancillary_carriers && providerInfo.ancillary_carriers.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {providerInfo.ancillary_carriers.map((carrier: string, index: number) => (
                    <span key={index} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                      {carrier}
                    </span>
                  ))}
                </div>
              ) : (
                <span className="text-muted-foreground italic">Not set</span>
              )}
            </div>
          </div>

          <div>
            <Label className="text-sm font-medium text-muted-foreground">Workers Comp Carriers</Label>
            <div className="mt-2">
              {providerInfo.workers_comp_carriers && providerInfo.workers_comp_carriers.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {providerInfo.workers_comp_carriers.map((carrier: string, index: number) => (
                    <span key={index} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                      {carrier}
                    </span>
                  ))}
                </div>
              ) : (
                <span className="text-muted-foreground italic">Not set</span>
              )}
            </div>
          </div>

          <div>
            <Label className="text-sm font-medium text-muted-foreground">401k Vendor</Label>
            <p className="text-base mt-1">{providerInfo.k401_vendor || <span className="text-muted-foreground italic">Not set</span>}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold">Service Capabilities</h3>
      <div className="grid gap-6">
        <div className="flex items-center justify-between">
          <div>
            <Label>Mobile App</Label>
            <p className="text-sm text-muted-foreground">Does the PEO offer a mobile application?</p>
          </div>
          <Switch 
            checked={!!form?.mobile_app}
            onCheckedChange={(checked) => handleSwitchChange('mobile_app', checked)}
            disabled={saving}
          />
        </div>

        <div>
          <Label htmlFor="payroll_platform">Payroll Platform</Label>
          <Input
            id="payroll_platform"
            name="payroll_platform"
            value={form?.payroll_platform || ''}
            onChange={handleFieldChange}
            placeholder="e.g. ADP, Paychex, Gusto"
            disabled={saving}
          />
          <p className="text-xs text-muted-foreground mt-1">Primary payroll platform used by the PEO</p>
        </div>

        <TagInputField
          label="Medical Carriers"
          fieldName="medical_carriers"
          placeholder="e.g. Aetna, Blue Cross Blue Shield, Cigna"
          description="Medical insurance carriers available through the PEO"
          form={form}
          setForm={setForm}
          disabled={saving}
        />

        <TagInputField
          label="Ancillary Carriers"
          fieldName="ancillary_carriers"
          placeholder="e.g. Principal, MetLife, Aflac, Guardian"
          description="Ancillary insurance carriers available through the PEO"
          form={form}
          setForm={setForm}
          disabled={saving}
        />

        <TagInputField
          label="Workers Comp Carriers"
          fieldName="workers_comp_carriers"
          placeholder="e.g. AmTrust, Berkshire Hathaway, Liberty Mutual"
          description="Workers' compensation carriers available through the PEO"
          form={form}
          setForm={setForm}
          disabled={saving}
        />

        <div>
          <Label htmlFor="k401_vendor">401k Vendor</Label>
          <Input
            id="k401_vendor"
            name="k401_vendor"
            value={form?.k401_vendor || ''}
            onChange={handleFieldChange}
            placeholder="e.g. Fidelity, Vanguard, John Hancock"
            disabled={saving}
          />
          <p className="text-xs text-muted-foreground mt-1">401k vendors available through the PEO</p>
        </div>
      </div>
    </div>
  );
};